// documentFacts — where a material stands as a document, and what it should
// contain, as the client reads them (`Resource.DocumentState` + the plan,
// server-projected on each library row as `document`). Pure: types and the
// wire parse.
//
// Parse, don't validate: an unknown state is kept as `unknown`, which the
// Studio shows like Planned (neutral); an unknown kind is dropped.

export type { DocumentFacts, DocumentKind };
export { parseDocumentFacts };

import { fields, str } from "./wireShape.js";

type DocumentState =
    | "generated"
    | "planned"
    | "being_written"
    | "empty"
    | "removed"
    | "unknown";

type DocumentKind =
    | "course"
    | "module"
    | "chapter"
    | "page"
    | "quiz"
    | "handout";

type DocumentFacts = {
    readonly state: DocumentState;
    // What it was planned as. Absent: never planned (authored directly).
    readonly kind?: DocumentKind;
    // What it should contain. Absent: no brief (a container of parts, or
    // never planned).
    readonly brief?: string;
};

const stateOf = (raw: string | undefined): DocumentState => {
    switch (raw) {
        case "generated":
        case "planned":
        case "being_written":
        case "empty":
        case "removed":
            return raw;
        default:
            return "unknown";
    }
};

const kindOf = (raw: string | undefined): DocumentKind | undefined => {
    switch (raw) {
        case "course":
        case "module":
        case "chapter":
        case "page":
        case "quiz":
        case "handout":
            return raw;
        default:
            return undefined;
    }
};

const parseDocumentFacts = (
    raw: unknown,
): { readonly document?: DocumentFacts } => {
    const f = fields(raw);
    if (f === undefined) return {};
    const kind = kindOf(str(f.get("kind")));
    const brief = str(f.get("brief"));
    return {
        document: {
            state: stateOf(str(f.get("state"))),
            ...(kind === undefined ? {} : { kind }),
            ...(brief === undefined ? {} : { brief }),
        },
    };
};
